// Fon vazifalari reestri — `xsib-platform` `job` moduli bilan kontrakt.
//
//   GET /api/jobs
//        → JobView[]   ⚠️ SAHIFALASH YO'Q (`JobController#registry`)
//   GET /api/jobs/runs?service&job&outcome&from&to&page&size
//        → {rows, page, size, hasNext}   (`AuditPage` bilan AYNI shakl)
//
// Huquq — BITTASI, ikkala endpoint uchun ham: `job.run.view`.
// ⚠️ `.read` EMAS: `identity.seed_system_roles()` `%.read` bilan tugagan
// huquqni "Kuzatuvchi"/"Qo'llab-quvvatlash" rollariga O'Z-O'ZIDAN
// qo'shadi (`V202608140200` migratsiyasi izohi). Bu sahifa mijoz
// ma'lumotini emas, PLATFORMANING ichki holatini ko'rsatadi.
//
// =====================================================================
// ⚠️ SAHIFA NIMA UCHUN BOR — 2026-08-13, `EventPullJob`
// =====================================================================
// Qurilma `active=false` bo'lgani uchun `EventPullJob` kun bo'yi hech
// narsa qilmadi. U muvaffaqiyatda log YOZMAYDI, ya'ni "jurnalda yo'q"
// degan belgi IKKI xil narsani anglatardi:
//     a) job yurdi, ish yo'q edi   (normal)
//     b) job umuman yurmadi        (o'lik)
// Ularni ajratib bo'lmasdi. Butun sahifaning maqsadi — shu ikkisini
// BIR QARASHDA farqlash. Shuning uchun bu yerda "sog'liq bahosi"
// (0..100 ball) YO'Q va bo'lmaydi ham: bir songa siqilgan holat aynan
// o'sha ajratmani yana yo'q qilardi. Xom maydonlar ko'rsatiladi,
// xulosani operator chiqaradi.
//
// =====================================================================
// ⚠️ SPETSIFIKATSIYA BILAN IKKI FARQ (jonli DTO'lar bo'yicha tekshirilgan)
// =====================================================================
// 1. `/api/jobs/runs` da `from`/`to` MAJBURIY. `JobRunQuery` kompakt
//    konstruktori ularsiz `IllegalArgumentException` tashlaydi (400).
//    Dastlabki shartnomada ular ixtiyoriy deb yozilgan edi — bu yerda
//    ular DOIM yuboriladi (`AuditPage` bilan bir xil qaror va bir xil
//    sabab: tarix jadvali o'sadi).
// 2. Reestr SERVERDA saralangan ("muammolilari tepada" —
//    `JobRegistryRepository#registry`). Panel uni QAYTA saralamaydi:
//    ikkinchi tartib qoidasi bo'lsa, ikkalasi ajralib ketardi va
//    "nega bu qator o'rtada?" degan savol tug'ilardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * Yurish natijasi — backend `uz.xsib.common.job.JobOutcome` nusxasi.
 *
 * ⚠️ `OperationStatus` (`OK/PARTIAL/FAILED`) NING NUSXASI EMAS va unga
 * birlashtirib bo'lmaydi: bu yerda TO'RTINCHI qiymat bor —
 * `NOTHING`. "Hech narsa qilmadim" muvaffaqiyat ham, xato ham emas, u
 * o'zi ma'noli natija. Bazada ham shu ro'yxat `job_run_outcome_ck`
 * cheklovi bilan qotirilgan.
 */
export const JOB_OUTCOMES = ['OK', 'NOTHING', 'PARTIAL', 'FAILED'] as const
export type JobOutcome = (typeof JOB_OUTCOMES)[number]

/** `JobRunQuery.MAX_PAGE_SIZE` — kattaroq so'ralsa server JIMGINA shuni qo'yadi */
export const RUN_MAX_PAGE_SIZE = 200

// --- Reestr ------------------------------------------------------------------

const jobSchema = z.object({
  service: z.string(),
  job: z.string(),
  schedule: z.string().nullish(),
  scheduled: z.boolean(),
  seenAt: instantSchema,
  lastStartedAt: instantSchema.nullish(),
  lastDurationMs: z.number().nullish(),
  lastOutcome: z.enum(JOB_OUTCOMES).nullish(),
  lastItems: z.number().nullish(),
  lastMessage: z.string().nullish(),
  lastMessageCode: z.string().nullish(),
  lastMessageParams: z.record(z.string(), z.string()).nullish(),
  lastError: z.string().nullish(),
  lastOkAt: instantSchema.nullish(),
  runs: z.number(),
  staleSeconds: z.number().nullish(),
})

export interface Job {
  service: string
  job: string
  /** `"cron 0 30 2 * * *"` yoki `"har 5m"`. `null` — e'lon qilinmagan */
  schedule: string | null
  /**
   * ⚠️ ENG MUHIM MAYDON. `false` = klass bor, bean bor, kod to'g'ri —
   * lekin rejalashtiruvchi uni USHLAB TURMAYAPTI, ya'ni job hech qachon
   * yurmaydi. Hozir bu jonli holat: `xsib-crm` da `@EnableScheduling`
   * umuman yo'q va `LeadRetentionJob` o'lik.
   */
  scheduled: boolean
  /** Servis oxirgi marta ko'tarilib bu jobni e'lon qilgan payt */
  seenAt: string
  /** ⚠️ `null` — HECH QACHON yurmagan (bu YO'QLIK emas, MUAMMO) */
  lastStartedAt: string | null
  lastDurationMs: number | null
  lastOutcome: JobOutcome | null
  lastItems: number | null
  /**
   * ⚠️ SERVER TILIDAGI matn — ZAXIRA, asosiy yo'l EMAS (N5).
   * Chizishda faqat {@link lastMessageCode} noma'lum bo'lganda
   * ishlatiladi. Butunlay tashlab yuborish yangi kod eski panelga
   * tushgan lahzada xabarni JIMGINA yo'qotardi.
   */
  lastMessage: string | null
  /** `null` — N5 dan OLDIN yozilgan qator; matn zaxirasiga tushadi */
  lastMessageCode: string | null
  /** Sana bu yerda ISO shaklida — formatlash PANELNING ishi */
  lastMessageParams: Readonly<Record<string, string>>
  lastError: string | null
  /**
   * Oxirgi XATOSIZ yurish (`OK` yoki `NOTHING`).
   *
   * ⚠️ Bu "oxirgi marta ISH BAJARGAN" DEGANI EMAS: `NOTHING` ham shu
   * ustunni yangilaydi (migratsiya `V202608140100`). Ya'ni 2026-08-13
   * dagi holatda u har 5 daqiqada yangilanib turardi. "Oxirgi marta
   * qachon rostdan ish bajardi" degan savolga faqat TARIX javob beradi
   * — shuning uchun sahifada tarix oynasi bor va unda `outcome=OK`
   * filtri.
   */
  lastOkAt: string | null
  runs: number
  /**
   * `lastStartedAt` dan beri necha sekund — SERVER soati bo'yicha.
   *
   * ⚠️ Panel buni QAYTA HISOBLAMAYDI, garchi `lastStartedAt` javobda
   * bor bo'lsa ham. Sabab `JobView.of` javadoc'ida: brauzer soati
   * serverdan farq qiladi va ikki qatorni solishtirish nozik tarzda
   * noto'g'ri bo'lardi. `null` — hech qachon yurmagan (`0` EMAS: nol
   * "hozirgina yurdi" degani bo'lardi, ya'ni eng yomon holat eng
   * yaxshisiga o'xshab ko'rinardi).
   */
  staleSeconds: number | null
}

function toJob(raw: z.infer<typeof jobSchema>): Job {
  return {
    service: raw.service,
    job: raw.job,
    schedule: raw.schedule ?? null,
    scheduled: raw.scheduled,
    seenAt: raw.seenAt,
    lastStartedAt: raw.lastStartedAt ?? null,
    lastDurationMs: raw.lastDurationMs ?? null,
    lastOutcome: raw.lastOutcome ?? null,
    lastItems: raw.lastItems ?? null,
    lastMessage: raw.lastMessage ?? null,
    lastMessageCode: raw.lastMessageCode ?? null,
    lastMessageParams: raw.lastMessageParams ?? {},
    lastError: raw.lastError ?? null,
    lastOkAt: raw.lastOkAt ?? null,
    runs: raw.runs,
    staleSeconds: raw.staleSeconds ?? null,
  }
}

/**
 * Butun reestr — BIR SO'ROVDA, sahifalashsiz.
 *
 * ⚠️ `DataTable` bu yerda ISHLATILMAYDI va bu ataylab: endpoint
 * sahifalanmaydi (`JobController#registry` javadoc'i — har jobga bitta
 * qator, jadval o'smaydi). Sahifalanadigan jadval komponentini
 * sahifalanmaydigan endpointga ulash SOXTA SHARTNOMA bo'lardi:
 * "keyingi sahifa" tugmasi hech qachon sinalmagan yo'lni ochardi.
 */
export async function fetchJobs(signal?: AbortSignal): Promise<readonly Job[]> {
  const response = await api.get<unknown>('/api/jobs', { ...(signal ? { signal } : {}) })
  return z.array(jobSchema).parse(response.data).map(toJob)
}

// --- Yurish tarixi -----------------------------------------------------------

const jobRunSchema = z.object({
  /**
   * ⚠️ SON, UUID EMAS (`platform.job_run.id` = `BIGINT`, `JobRunView.id`
   * = `long`) — `auditApi.ts` dagi bilan bir xil tuzoq va bir xil
   * yechim. U yerda `uuidSchema` turgan va zod HAR javobni rad etardi:
   * sahifa mangu skeletonda qolar, xato ham ko'rinmasdi.
   */
  id: z.union([z.number(), z.string()]),
  service: z.string(),
  job: z.string(),
  startedAt: instantSchema,
  durationMs: z.number(),
  outcome: z.enum(JOB_OUTCOMES),
  items: z.number(),
  /** ⚠️ JSONB OBYEKT (`Map<String, Object>`), satr emas */
  detail: z.unknown().nullish(),
  message: z.string().nullish(),
  messageCode: z.string().nullish(),
  messageParams: z.record(z.string(), z.string()).nullish(),
  error: z.string().nullish(),
})

/**
 * `detail` ni odam o'qiydigan matnga o'giradi (`auditApi.toDetailText`
 * bilan bir xil qaror).
 *
 * Xom JSON chiroyli formatlangan holda ko'rsatiladi: sanoqlarning
 * NOMLARI har jobda boshqa (`JobRun#counted` — "devices", "stored",
 * "failed"), ya'ni ularni maydonma-maydon chizib bo'lmaydi. Aynan
 * tafsilot kerak bo'lganda odam xomini ko'rgani ma'qul.
 */
function toDetailText(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'string') return raw.trim() === '' ? null : raw
  try {
    const text = JSON.stringify(raw, null, 2)
    return text === undefined || text === '{}' ? null : text
  } catch {
    return null
  }
}

export interface JobRun {
  id: string
  service: string
  job: string
  startedAt: string
  durationMs: number
  outcome: JobOutcome
  /**
   * Bajarilgan ishning UMUMIY hajmi.
   *
   * ⚠️ Bu aniq o'lchov emas, FAOLLIK BELGISI: turli nomdagi sanoqlar
   * qo'shiladi (`JobRun#totalItems`). Aniq ajratma `detail` da.
   */
  items: number
  detail: string | null
  /** ⚠️ SERVER TILIDA — zaxira (N5), `messageCode` dan keyin turadi */
  message: string | null
  messageCode: string | null
  messageParams: Readonly<Record<string, string>>
  error: string | null
}

function toJobRun(raw: z.infer<typeof jobRunSchema>): JobRun {
  return {
    id: String(raw.id),
    service: raw.service,
    job: raw.job,
    startedAt: raw.startedAt,
    durationMs: raw.durationMs,
    outcome: raw.outcome,
    items: raw.items,
    detail: toDetailText(raw.detail),
    message: raw.message ?? null,
    messageCode: raw.messageCode ?? null,
    messageParams: raw.messageParams ?? {},
    error: raw.error ?? null,
  }
}

/** ⚠️ `total` YO'Q, faqat `hasNext` — `AuditPage` bilan bir xil envelope */
const jobRunPageSchema = z.object({
  rows: z.array(jobRunSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

export interface JobRunFilter {
  service: string | null
  job: string | null
  outcome: JobOutcome | null
  /** ISO-8601 UTC — ⚠️ MAJBURIY (`JobRunQuery` usiz 400 qaytaradi) */
  from: string
  to: string
}

/**
 * Tarix sahifasi.
 *
 * ⚠️ QIDIRUV MAYDONI YO'Q va bu ataylab: `JobRunQuery` da matnli qidiruv
 * umuman yo'q (`service`/`job`/`outcome`/sana — hammasi strukturaviy).
 * Maydonni chizib, uni jimgina e'tiborsiz qoldirish "qidirdim —
 * topilmadi" degan YOLG'ON javob bo'lardi (`StockMovementsPage` bilan
 * bir xil qaror).
 */
export function fetchJobRunPage(
  filter: JobRunFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<JobRun>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/jobs/runs', {
      signal,
      params: {
        ...(filter.service ? { service: filter.service } : {}),
        ...(filter.job ? { job: filter.job } : {}),
        ...(filter.outcome ? { outcome: filter.outcome } : {}),
        from: filter.from,
        to: filter.to,
        // Backend sahifani 0 dan sanaydi — boshqa ro'yxat endpointlari
        // bilan bir xil andoza
        page: state.page - 1,
        size: Math.min(state.pageSize, RUN_MAX_PAGE_SIZE),
      },
    })
    const parsed = jobRunPageSchema.parse(response.data)
    return {
      items: parsed.rows.map(toJobRun),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Job xabari — TARJIMA QILINGAN matn (N5).
 *
 * <h2>⚠️ NEGA SERVER TAYYOR JUMLA BERMAYDI</h2>
 *
 * Ilgari bergan va o'lchov shuni ko'rsatdi (2026-08-15, jonli
 * `af-isp`, tenant tili `ru`): `/jobs` da 35 o'rinda server yozgan
 * o'zbekcha matn turardi, yonidagi ustunlar esa ruscha/inglizcha.
 * Til almashtirilganda o'sha kataklar O'ZGARMASDI.
 *
 * Sabab: matn `platform.job.last_message` ga YOZISH paytida tushadi,
 * job esa `@Scheduled` orqali yuradi — o'sha lahzada `Accept-Language`
 * umuman yo'q. Ya'ni serverda tarjima qilishning texnik imkoni yo'q.
 *
 * Kod noma'lum bo'lsa serverning matni faqat o'zbek panelida chiziladi:
 * u faqat o'zbekcha yoziladi, boshqa tilda uni ko'rsatmaslik egasi qarori.
 *
 * @returns tarjima yoki zaxira matn; ikkalasi ham yo'q bo'lsa `null`
 */
export function jobMessageText(
  code: string | null,
  params: Readonly<Record<string, string>>,
  fallback: string | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  const translated = code === null ? '' : t(`jobs.message.${code}`, { ...params, defaultValue: '' })
  if (translated !== '') return translated
  return t('common.langCode', { defaultValue: '' }) === 'uz' ? fallback : null
}

/**
 * Vazifaning holati — XOM MAYDONLARDAN kelib chiqadi, hech qanday
 * "ball" yoki og'irlik yig'indisi YO'Q.
 *
 * ⚠️ TARTIB — bu funksiyaning butun mazmuni. Har shart oldingisidan
 * KEYIN tekshiriladi va sabab har birida boshqa:
 *
 *   1. `DEAD`    — rejalashtiruvchi ushlamayapti. Eng JIM nosozlik:
 *                  xato yo'q, jurnal bo'sh, kod to'g'ri ko'rinadi.
 *                  Shuning uchun eng tepada va sahifada alohida banner.
 *   2. `NEVER`   — ro'yxatdan o'tgan, lekin hali yurmagan. `IDLE` dan
 *                  ATAYLAB ajratilgan: "ishi yo'q edi" bilan "umuman
 *                  ishga tushmagan" bir xil ko'rinishi 2026-08-13
 *                  ning aynan takrori bo'lardi.
 *   3. `FAILED`  — oxirgi yurish istisno bilan tugadi.
 *   4. `OVERDUE` — jadvalga ko'ra allaqachon yurishi kerak edi.
 *                  `PARTIAL` DAN TEPADA: qisman bajarilgan ish baribir
 *                  ish, umuman yurmagan job esa to'xtagan mexanizm.
 *   5. `PARTIAL` — ishning bir qismi yiqildi.
 *   6. `IDLE`    — yurdi, ishi yo'q edi. ⚠️ Bu NORMAL holat va u
 *                  QIZIL EMAS. Lekin uzoq davom etsa — 2026-08-13
 *                  imzosi, shuning uchun qator baribir ko'rinadi va
 *                  yonida `lastOkAt`/`staleSeconds` xom holda turadi.
 *   7. `OK`      — oxirgi yurish ish bajarib tugadi.
 */
export const JOB_STATES = [
  'DEAD',
  'NEVER',
  'FAILED',
  'OVERDUE',
  'PARTIAL',
  'IDLE',
  'OK',
] as const
export type JobState = (typeof JOB_STATES)[number]

/**
 * Kechikish koeffitsienti.
 *
 * ⚠️ BITTA o'tkazib yuborilgan yurish nosozlik EMAS: servis qayta ishga
 * tushgan bo'lishi, konteyner ko'chirilgan bo'lishi mumkin. Uchtasi
 * ketma-ket o'tkazib yuborilishi esa allaqachon naqsh. Son shu yerda,
 * BITTA joyda turadi — sahifada tarqatib yozilsa ular ajralib ketardi.
 */
export const OVERDUE_FACTOR = 3

const ISO_DURATION = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i
const SUFFIXED = /^(\d+(?:\.\d+)?)\s*(ms|s|m|h|d)?$/i
const UNIT_SECONDS: Record<string, number> = { ms: 0.001, s: 1, m: 60, h: 3600, d: 86400 }

/**
 * Jadvaldan kutilayotgan oraliq (sekund) — bilib bo'lmasa `null`.
 *
 * Satr shakli `JobRegistrar#describe` da yasaladi:
 *   `"har 5m"` · `"har PT30S"` · `"har 300000ms"` · `"har 300000"`
 *   `"cron 0 30 2 * * *"`
 *
 * ⚠️ `cron` UCHUN `null` QAYTARADI va bu ataylab. Cron ifodasini
 * hisoblash uchun panelga kutubxona kerak bo'lardi; taxminan hisoblash
 * esa bundan ham yomon — kunlik job uchun "40 daqiqa yurmadi" normal,
 * daqiqalik job uchun falokat, ya'ni noto'g'ri oraliq noto'g'ri
 * OGOHLANTIRISH beradi. Bilmaganda jim turish — bilgandek ko'rinishdan
 * afzal: `staleSeconds` XOM holda baribir ko'rsatiladi va xulosani
 * operator chiqaradi.
 *
 * ⚠️ Birliksiz son MILLISEKUND: Spring `@Scheduled` ning
 * `fixedDelayString`/`fixedRateString` uchun qoidasi shu.
 */
export function expectedIntervalSeconds(schedule: string | null): number | null {
  if (!schedule) return null
  const text = schedule.trim()
  if (!text.toLowerCase().startsWith('har ')) return null
  const value = text.slice(4).trim()
  if (value === '') return null

  const iso = ISO_DURATION.exec(value)
  if (iso) {
    const [, days, hours, minutes, seconds] = iso
    const total =
      Number(days ?? 0) * 86400 +
      Number(hours ?? 0) * 3600 +
      Number(minutes ?? 0) * 60 +
      Number(seconds ?? 0)
    return total > 0 ? total : null
  }

  const suffixed = SUFFIXED.exec(value)
  if (!suffixed) return null
  const amount = Number(suffixed[1])
  if (!Number.isFinite(amount) || amount <= 0) return null
  const unit = UNIT_SECONDS[(suffixed[2] ?? 'ms').toLowerCase()]
  if (unit === undefined) return null
  const total = amount * unit
  return total > 0 ? total : null
}

/**
 * Vazifa jadvalidan KECHIKKANMI.
 *
 * ⚠️ Oraliq noma'lum bo'lsa (cron, jadval yo'q) `false` qaytaradi —
 * "kechikmagan" degani emas, "ayta olmayman" degani. Ikkalasini
 * farqlash uchun sahifada `staleSeconds` va `schedule` XOM holda
 * yonma-yon ko'rsatiladi.
 */
export function isOverdue(job: Job): boolean {
  const interval = expectedIntervalSeconds(job.schedule)
  if (interval === null || job.staleSeconds === null) return false
  return job.staleSeconds > interval * OVERDUE_FACTOR
}

export function jobState(job: Job): JobState {
  if (!job.scheduled) return 'DEAD'
  if (job.lastStartedAt === null) return 'NEVER'
  if (job.lastOutcome === 'FAILED') return 'FAILED'
  if (isOverdue(job)) return 'OVERDUE'
  if (job.lastOutcome === 'PARTIAL') return 'PARTIAL'
  if (job.lastOutcome === 'NOTHING') return 'IDLE'
  return 'OK'
}

/**
 * Bu holat ARALASHUV talab qiladimi.
 *
 * ⚠️ `IDLE` bu yerda YO'Q va bu qarorning eng nozik joyi. "Ishi yo'q
 * edi" — ko'p job uchun kunning ko'p qismidagi ODATIY holat (navbat
 * bo'sh, muddati o'tgan yozuv yo'q). Uni ogohlantirishga qo'shish
 * sahifani doimiy sariq qilardi va bir haftadan keyin hech kim unga
 * qaramasdi — ya'ni bo'ri qichqirgan cho'pon. 2026-08-13 dagi holat
 * esa `IDLE` ning O'ZIDAN emas, uning UZOQ DAVOM ETISHIDAN ko'rinadi
 * va buni faqat tarix ko'rsata oladi.
 */
export function needsAttention(state: JobState): boolean {
  return state === 'DEAD' || state === 'NEVER' || state === 'FAILED' || state === 'OVERDUE'
}

// --- Ogohlantirish qorovuli --------------------------------------------------
//
//   GET /api/jobs/alerts  → JobAlertStatus   (huquq: `job.run.view` — reestr
//                                             bilan AYNI BITTA huquq)
//
// ⚠️ NEGA BU REESTRDAN AJRATILGAN
// Reestr "job qanday" deb so'raydi, bu yer esa BIR DARAJA YUQORIDA:
// "buni kimdir KO'RDIMI". Farq nazariy emas — `BillingJob` 22 daqiqa
// yiqilib turganda reestr TO'G'RI edi va nosozlik unda ko'rinardi;
// yetishmagan narsa XABAR edi (`JobAlertStatus` javadoc'i).
//
// ⚠️ ENG QIMMATLI MAYDON — `lastScanAt`
// Qorovul o'z o'limi haqida xabar bera olmaydi: u xabar yuboradigan kod
// bilan BIRGA o'ladi. Ya'ni "ogohlantirish kelmadi" degani "hammasi
// joyida" degani EMAS — jimlikning ikki sababi bor va ularni faqat shu
// sana ajratadi. Shuning uchun u sahifada ro'yxatdan YUQORIDA turadi va
// hech qanday shart ostida yashirilmaydi.

/**
 * Ogohlantirish turi — backend `JobAlertKind` enum'ining NUSXASI.
 *
 * ⚠️ OLTITASI HAM YOZILGAN, garchi `open` ro'yxatida ulardan faqat
 * to'rttasi uchrasa ham. `RECOVERED` va `HEARTBEAT` — XABAR turlari,
 * ular `platform.job_alert.kind` ustuniga yozilmaydi
 * (`JobAlertServiceImpl.claim` ularni yuborish paytida hisoblaydi).
 * Ro'yxatni qisqartirsak, backend ularni bir kun javobga qo'shsa `zod`
 * BUTUN javobni rad etardi va sahifa bo'sh qolardi — nosozlik eng
 * kerakli paytda ko'rinmasdi.
 */
export const JOB_ALERT_KINDS = [
  'NEVER_RAN',
  'OVERDUE',
  'FAILED',
  'NO_SUCCESS',
  'RECOVERED',
  'HEARTBEAT',
] as const
export type JobAlertKind = (typeof JOB_ALERT_KINDS)[number]

/**
 * Nosozlik turlarining DARAJASI — ekrandagi tartib ham shu.
 *
 * ⚠️ TARTIB `JobHealthEvaluator` DAN KO'CHIRILGAN, o'ylab topilmagan.
 * Bir job bir vaqtda bir nechta shartga tushishi mumkin va backend
 * ulardan bittasini AYNAN shu tartibda tanlaydi. Panel boshqa tartib
 * ko'rsatsa, operator ro'yxatni backend muhim deb hisoblagandan
 * boshqacha o'qirdi.
 *
 * ⚠️ `OVERDUE` `FAILED` DAN YUQORI va bu ataylab: yiqilayotgan job hech
 * bo'lmasa YURADI va har yurishida iz qoldiradi; yurmayotgan job esa
 * JIM — aynan jimlik bu loyihada nosozlikni soatlab yashirgan.
 */
export const JOB_FAULT_ORDER = ['NEVER_RAN', 'OVERDUE', 'FAILED', 'NO_SUCCESS'] as const

const jobAlertOpenSchema = z.object({
  service: z.string(),
  job: z.string(),
  kind: z.enum(JOB_ALERT_KINDS),
  reason: z.string().nullish(),
  // ⚠️ IKKALASI HAM `.nullish()`, `.nullable()` EMAS (N1 saboqi).
  //    Server `default-property-inclusion: non_null` bilan ishlaydi,
  //    ya'ni `null` maydon JSON'dan BUTUNLAY tushib qoladi va
  //    `.nullable()` `undefined` ni RAD ETARDI — sahifa `ZodError`
  //    bilan butunlay ochilmasdi. Bu yerda esa `null` ODATIY hol:
  //    N3 dan oldin ochilgan ogohlantirishlarda kod yo'q.
  reasonCode: z.string().nullish(),
  reasonParams: z.record(z.string(), z.string()).nullish(),
  firstSeenAt: instantSchema,
  notifiedAt: instantSchema.nullish(),
  notifyCount: z.number(),
  recovered: z.boolean(),
})

/**
 * ⚠️ EKSPORT QILINGAN — sinov uchun (`tests/job-alerts.mjs`), va bu
 * `subscriberSchema` bilan bir xil qaror: qorovul javobning o'qilishini
 * MANBA matnida emas, HAQIQIY sxemani bajarib tekshiradi.
 */
export const jobAlertStatusSchema = z.object({
  open: z.array(jobAlertOpenSchema),
  lastScanAt: instantSchema.nullish(),
  lastHeartbeatAt: instantSchema.nullish(),
  scans: z.number(),
  alertsSent: z.number(),
})

export interface JobAlert {
  service: string
  job: string
  kind: JobAlertKind
  /**
   * Serverning TAYYOR o'zbekcha jumlasi — ZAXIRA, asosiy yo'l emas (N3).
   *
   * ⚠️ Saqlanadi va chizishda ishlatiladi, lekin faqat `reasonCode`
   * noma'lum bo'lganda. Uni butunlay tashlab yuborish yangi kod eski
   * panelga tushgan lahzada ogohlantirishni JIMGINA yo'qotardi.
   */
  reason: string | null
  /** `null` — N3 dan OLDIN ochilgan qator; matn zaxirasiga tushadi */
  reasonCode: string | null
  /** Sana bu yerda ISO shaklida — formatlash PANELNING ishi */
  reasonParams: Readonly<Record<string, string>>
  firstSeenAt: string
  /** `null` = qator bor, xabar YO'Q — sahifada alohida ko'rsatiladi */
  notifiedAt: string | null
  notifyCount: number
  /** Job sog'aygan; «tiklandi» xabari navbatda turibdi */
  recovered: boolean
}

export interface JobAlertStatus {
  open: readonly JobAlert[]
  /** ⚠️ `null` = qorovul hali BIR MARTA HAM qaramagan */
  lastScanAt: string | null
  lastHeartbeatAt: string | null
  scans: number
  alertsSent: number
}

export async function fetchJobAlerts(signal?: AbortSignal): Promise<JobAlertStatus> {
  const response = await api.get<unknown>('/api/jobs/alerts', { ...(signal ? { signal } : {}) })
  const parsed = jobAlertStatusSchema.parse(response.data)
  return {
    open: parsed.open.map((row) => ({
      service: row.service,
      job: row.job,
      kind: row.kind,
      reason: row.reason ?? null,
      reasonCode: row.reasonCode ?? null,
      reasonParams: row.reasonParams ?? {},
      firstSeenAt: row.firstSeenAt,
      notifiedAt: row.notifiedAt ?? null,
      notifyCount: row.notifyCount,
      recovered: row.recovered,
    })),
    lastScanAt: parsed.lastScanAt ?? null,
    lastHeartbeatAt: parsed.lastHeartbeatAt ?? null,
    scans: parsed.scans,
    alertsSent: parsed.alertsSent,
  }
}

/**
 * Qorovulning O'Z holati — `lastScanAt` va `lastHeartbeatAt` uchun.
 *
 *   · `NEVER` — hech qachon bo'lmagan
 *   · `STALE` — bo'lgan, lekin ancha oldin
 *   · `FRESH` — yaqinda
 *
 * ⚠️ VAQT PARAMETR, ichkarida `Date.now()` YO'Q — `JobHealthEvaluator`
 * dagi bilan bir xil sabab: chegara holatlarini ("1 sekund oldin",
 * "1 sekund keyin") faqat shunday sinash mumkin.
 */
export type WatchFreshness = 'NEVER' | 'STALE' | 'FRESH'

export function watchFreshness(
  at: string | null,
  nowMs: number,
  staleSeconds: number,
): WatchFreshness {
  if (at === null) return 'NEVER'
  const ms = Date.parse(at)
  // ⚠️ O'qib bo'lmaydigan sana «yaxshi» deb hisoblanmaydi: buzuq qiymat
  // ekranda "hammasi joyida" bo'lib ko'rinardi — aynan shu sahifa
  // ochmoqchi bo'lgan yolg'on.
  if (!Number.isFinite(ms)) return 'NEVER'
  return nowMs - ms > staleSeconds * 1000 ? 'STALE' : 'FRESH'
}

/**
 * Skaner qancha vaqtda bir qaraydi — O'LCHANGAN, taxmin emas:
 * `xsib.notify.job-alerts.scan-interval` = `1m`
 * (`xsib-notify/src/main/resources/application-Staging.yml`).
 *
 * ⚠️ Chegara o'sha davrning O'ZI EMAS, 15 barobari. Bitta o'tkazib
 * yuborilgan skaner nosozlik emas: servis qayta ko'tarilgan, konteyner
 * ko'chirilgan bo'lishi mumkin — sahifa esa har daqiqada qizarardi va
 * bir haftadan keyin unga hech kim qaramasdi. 15 daqiqalik jimlik esa
 * allaqachon tushuntirishni talab qiladi.
 */
export const SCAN_STALE_SECONDS = 15 * 60

/**
 * «Tirikman» xabari sutkada bir marta ketadi
 * (`JobHealthProperties.heartbeatEvery` — kodda qotirilgan 24 soat).
 *
 * ⚠️ Chegara 26 soat: aynan 24 soat qo'yilsa xabar biroz kechikkan
 * kuni ham bekordan qizarardi.
 */
export const HEARTBEAT_STALE_SECONDS = 26 * 3600

export interface JobAlertGroup {
  kind: JobAlertKind
  rows: readonly JobAlert[]
}

/**
 * Ochiq ogohlantirishlarni TURI bo'yicha guruhlaydi —
 * {@link JOB_FAULT_ORDER} tartibida.
 *
 * ⚠️ NEGA GURUH, TEKIS QAYTA SARALASH EMAS. Server javobi allaqachon
 * saralangan: xabar YUBORILMAGANLARI birinchi ("topilgan, lekin hech
 * kim bilmaydi" — eng jim holat). Tekis qayta saralash o'sha tartibni
 * jimgina yo'q qilardi va panelda ikkinchi tartib qoidasi paydo
 * bo'lardi — ular vaqt o'tib ajralib ketardi (`fetchJobs` ustidagi izoh
 * bilan bir xil sabab). Guruh ICHIDA server tartibi o'zgarmay qoladi.
 *
 * ⚠️ SOG'AYGANLAR ALOHIDA: ular nosozlik EMAS, «tiklandi» xabari
 * navbatda turgan qatorlar. Nosozliklar bilan aralashtirilsa ro'yxat
 * bor muammodan kattaroq ko'rinardi.
 *
 * ⚠️ NOMA'LUM TUR TASHLANMAYDI — u eng oxirida chiqadi. Qorovul
 * sahifasida jimgina yo'q qilish eng yomon xatti-harakat bo'lardi.
 */
export function groupAlerts(open: readonly JobAlert[]): {
  faults: readonly JobAlertGroup[]
  recovered: readonly JobAlert[]
} {
  const rank = (kind: JobAlertKind) => {
    const at = (JOB_FAULT_ORDER as readonly string[]).indexOf(kind)
    return at === -1 ? JOB_FAULT_ORDER.length : at
  }

  const byKind = new Map<JobAlertKind, JobAlert[]>()
  const recovered: JobAlert[] = []
  for (const row of open) {
    if (row.recovered) {
      recovered.push(row)
      continue
    }
    const bucket = byKind.get(row.kind)
    if (bucket) bucket.push(row)
    else byKind.set(row.kind, [row])
  }

  const faults = [...byKind.entries()]
    .map(([kind, rows]) => ({ kind, rows }))
    .sort((a, b) => rank(a.kind) - rank(b.kind))

  return { faults, recovered }
}
